import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Card from './Card';
import PostScoreModal from './PostScoreModal';
import LeavePageModal from './LeavePageModal';
import PhotoSubmissionModal from './PhotoSubmissionModal';
import ThemeSelectionModal from './ThemeSelectionModal';
import CreditsModal from './CreditsModal';

import './MemoryGame.css';

const MemoryGame = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState('');
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [showPostScore, setShowPostScore] = useState(false);
  const [showLeavePage, setShowLeavePage] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showThemeSelection, setShowThemeSelection] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  const [isGameComplete, setIsGameComplete] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [userPlacement, setUserPlacement] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [error] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('theme1');
  const [themeName, setThemeName] = useState('NicoleCodeGirl');
  
  // Sound effects
  const [sounds, setSounds] = useState({});
  
  // Animated messages
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [messageType, setMessageType] = useState('');

  // Function definitions

  const initializeSounds = () => {
    const soundFiles = {
      cardFlip: '/public/sounds/card-flip.mp3',
      match: '/public/sounds/match.mp3',
      shuffle: '/public/sounds/shuffle.mp3',
      win: '/public/sounds/win.mp3'
    };

    const soundObjects = {};
    Object.keys(soundFiles).forEach(key => {
      try {
        console.log(`Loading sound: ${key} from ${soundFiles[key]}`);
        soundObjects[key] = new Audio(soundFiles[key]);
        soundObjects[key].volume = 0.5;
        
        // Add event listeners for debugging
        soundObjects[key].addEventListener('loadstart', () => {
          console.log(`Sound ${key}: loadstart`);
        });
        soundObjects[key].addEventListener('canplay', () => {
          console.log(`Sound ${key}: canplay`);
        });
        soundObjects[key].addEventListener('error', (e) => {
          console.error(`Sound ${key}: error`, e);
        });
      } catch (err) {
        console.warn(`Could not load sound: ${key}`, err);
      }
    });
    setSounds(soundObjects);
  };

  const playSound = useCallback((soundName) => {
    console.log(`Attempting to play sound: ${soundName}`);
    if (sounds[soundName]) {
      try {
        sounds[soundName].currentTime = 0;
        sounds[soundName].play().catch(err => {
          console.warn(`Could not play sound: ${soundName}`, err);
        });
        console.log(`Sound ${soundName} played successfully`);
      } catch (err) {
        console.warn(`Error playing sound: ${soundName}`, err);
      }
    } else {
      console.warn(`Sound ${soundName} not found in sounds object`);
    }
  }, [sounds]);

  const preloadImages = useCallback(() => {
    // Simple, fast image preloading
    if (photos.length === 0) return;
    
    const gamePhotos = photos.slice(0, 18);
    console.log(`🖼️ Preloading ${gamePhotos.length} images...`);
    
    gamePhotos.forEach(photo => {
      const img = new Image();
      img.src = photo.path; // Use original path without cache busting
    });
  }, [photos]);

  const initializeGame = useCallback(() => {
    if (photos.length === 0) return;

    console.log('Initializing game with photos:', photos.length);

    // Create pairs of cards efficiently
    const cardPairs = [];
    
    // Randomly select 18 photos for 36 cards (18 pairs)
    const shuffledPhotos = [...photos].sort(() => Math.random() - 0.5);
    const selectedPhotos = shuffledPhotos.slice(0, 18); // Use 18 photos for 36 cards (18 pairs)
    
    console.log('Selected photos for game:', selectedPhotos.map(p => p.filename));
    
    // Create cards in a single loop for better performance
    for (let i = 0; i < 18; i++) {
      const photo = selectedPhotos[i];
      // Use original path without cache busting
      const cleanPath = photo.path.split('?')[0];
      cardPairs.push(
        { id: i * 2, photo: cleanPath, isFlipped: false, isMatched: false },
        { id: i * 2 + 1, photo: cleanPath, isFlipped: false, isMatched: false }
      );
    }

    // Shuffle cards efficiently
    const shuffledCards = cardPairs.sort(() => Math.random() - 0.5);
    console.log('Created cards:', shuffledCards.map(c => ({ id: c.id, photo: c.photo })));
    
    // Batch state updates for better performance
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setGameStarted(false);
    setIsGameComplete(false);
    setStartTime(null);
    setGameStartTime(null);
    
    // Play shuffle sound
    playSound('shuffle');
  }, [photos, playSound]);

  const handleGameComplete = useCallback(() => {
    console.log('🎉 Game completed! Calculating final score... [NEW VERSION]');
    
    // Calculate final score from when timer started to now
    const endTime = new Date();
    const calculatedScore = gameStartTime ? Math.floor((endTime - gameStartTime) / 1000) : 0;
    
    console.log('⏰ Game start time:', gameStartTime);
    console.log('⏰ Game end time:', endTime);
    console.log('🏆 Final score (seconds):', calculatedScore);
    console.log('👤 Current user:', currentUser);
    console.log('🎨 Current theme:', currentTheme);
    
    // Set final score and game complete state
    setFinalScore(calculatedScore);
    setIsGameComplete(true);
    
    // Play win sound after confetti sound (delay to let confetti sound play first)
    setTimeout(() => {
      playSound('win');
    }, 300); // 300ms delay to let confetti sound start first
    
    // Post score first, then show modal with gameId
    console.log('🚀 Posting score to server...');
    console.log('📊 Score data:', {
      player_name: currentUser,
      total_time: calculatedScore,
      theme: currentTheme
    });
    
    fetch('/postScore', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        player_name: currentUser,
        total_time: calculatedScore,
        theme: currentTheme
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        console.log('✅ Score posted successfully');
        // Store gameId for sharing
        if (data.gameId) {
          setGameId(data.gameId);
        }
        // Show placement in PostScoreModal before navigating
        if (data.placement) {
          setUserPlacement(data.placement);
        }
        // Now show the modal with gameId available
        setTimeout(() => {
          setShowPostScore(true);
          console.log('✅ PostScoreModal should now be visible with score:', calculatedScore, 'and gameId:', data.gameId);
        }, 50);
      } else {
        console.error('❌ Failed to post score:', data.error);
        showAnimatedMessage('Failed to save score', 'error');
        // Still show modal even if posting failed
        setTimeout(() => {
          setShowPostScore(true);
        }, 50);
      }
    })
    .catch(error => {
      console.error('❌ Error posting score:', error);
      showAnimatedMessage('Failed to save score', 'error');
      // Still show modal even if posting failed
      setTimeout(() => {
        setShowPostScore(true);
      }, 50);
    });
  }, [gameStartTime, playSound, currentUser, currentTheme]);

  const showAnimatedMessage = (text, type = 'info', avoidPositions = []) => {
    setMessage(text);
    setMessageType(type);
    setShowMessage(true);
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setShowMessage(false);
    }, 3000)
  };

  const handleCardClick = (cardId) => {
    if (flippedCards.length >= 2 || matchedCards.includes(cardId)) {
      return;
    }

    // Start the game timer on first card click
    if (!gameStarted) {
      console.log('🎮 Game started - setting timer');
      setGameStarted(true);
      const now = new Date();
      setStartTime(now);
      setGameStartTime(now);
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    // Play card flip sound
    playSound('cardFlip');

    if (newFlippedCards.length === 2) {
      const [firstCard, secondCard] = newFlippedCards;
      const firstCardData = cards.find(card => card.id === firstCard);
      const secondCardData = cards.find(card => card.id === secondCard);

      if (firstCardData && secondCardData && firstCardData.photo === secondCardData.photo) {
        // Match found
        setMatchedCards(prev => [...prev, firstCard, secondCard]);
        setFlippedCards([]);
        playSound('match');
        showAnimatedMessage('Match!', 'success');
      } else {
        // No match
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  const handleNewGame = () => {
    console.log('Starting new game - showing theme selection');
    setShowThemeSelection(true);
  };

  const handlePhotoSubmissions = () => {
    console.log('Opening photo submissions modal');
    setShowPhotoModal(true);
  };

  const handleShowContributors = () => {
    console.log('Opening contributors modal');
    setShowCreditsModal(true);
  };

  const handleThemeSelect = async (theme) => {
    console.log('Theme selected:', theme);
    setCurrentTheme(theme);
    setThemeName(theme === 'theme1' ? 'NicoleCodeGirl' : 'Community');
    setShowThemeSelection(false);
    
    try {
      // Load photos dynamically from the server API
      console.log(`Loading photos for theme: ${theme}`);
      const response = await fetch(`/api/photos?theme=${theme}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load photos: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`API returned ${data.length} photos for ${theme}`);
      
      // Use original photo paths without cache busting for better performance
      const photoList = data;
      
      setPhotos(photoList);
      console.log(`Loaded ${photoList.length} photos for ${theme}`);
      
    } catch (error) {
      console.error('Error loading photos:', error);
      // Fallback to empty array if API fails
      setPhotos([]);
    }
  };


  const handleLeavePage = () => {
    setShowLeavePage(false);
    navigate('/');
  };

  const handleStayOnPage = () => {
    setShowLeavePage(false);
  };

  // useEffect hooks
  useEffect(() => {
    const user = localStorage.getItem('currentUser');
    if (!user) {
      navigate('/');
      return;
    }
    setCurrentUser(user);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (gameStarted && !isGameComplete) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [gameStarted, isGameComplete]);

  useEffect(() => {
    const fixMobileZoom = () => {
      // Force correct viewport on mobile
      if (window.innerWidth <= 768) {
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
        }
        
        // Force zoom level
        document.body.style.zoom = '1';
        document.body.style.transform = 'scale(1)';
        document.body.style.transformOrigin = 'top left';
      }
    };

    // Fix on load
    fixMobileZoom();
    
    // Fix on orientation change
    window.addEventListener('orientationchange', () => {
      setTimeout(fixMobileZoom, 100);
    });

    // Fix on resize
    window.addEventListener('resize', fixMobileZoom);

    return () => {
      window.removeEventListener('orientationchange', fixMobileZoom);
      window.removeEventListener('resize', fixMobileZoom);
    };
  }, []);

  useEffect(() => {
    initializeSounds();
    setShowThemeSelection(true);
  }, []);

  useEffect(() => {
    if (photos.length > 0) {
      // Preload images and initialize game immediately
      preloadImages();
      initializeGame();
    }
  }, [photos, preloadImages, initializeGame]);

  // Debug effect for photo modal
  useEffect(() => {
    console.log('PhotoModal state changed:', showPhotoModal);
  }, [showPhotoModal]);

  useEffect(() => {
    const matchedPairs = matchedCards.length / 2;
    console.log('Game completion check:', { 
      matchedCards: matchedCards.length, 
      matchedPairs: matchedPairs,
      totalCards: cards.length, 
      expectedPairs: cards.length / 2 
    });
    if (matchedCards.length > 0 && matchedPairs === cards.length / 2) {
      console.log('Game completed! Triggering completion handler...');
      setTimeout(() => {
        handleGameComplete();
      }, 100);
    }
  }, [matchedCards, cards.length, handleGameComplete]);


  // Render
  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }


  // Debug logging
  console.log('MemoryGame render:', {
    showThemeSelection,
    photos: photos.length,
    cards: cards.length,
    currentUser,
    error
  });

  return (
    <div className="memory-game">
      <Helmet>
        <title>Memory Game - {themeName}</title>
        <meta name="description" content={`Play the ${themeName} memory game`} />
      </Helmet>
      
      <Header 
        title={currentUser ? `Hello, ${currentUser}!` : 'Welcome to Memory Game!'}
        showLeaderboard={true}
        showNewPlayer={true}
        showNewGame={true}
        showStartTime={gameStarted}
        startTime={startTime}
        onNewGame={handleNewGame}
        onPhotoSubmissions={handlePhotoSubmissions}
        showContributors={true}
        onShowContributors={handleShowContributors}
      />

      <div className="game-container">
        {showThemeSelection && (
          <ThemeSelectionModal 
            isOpen={showThemeSelection}
            onClose={() => setShowThemeSelection(false)}
            onThemeSelect={handleThemeSelect}
            currentTheme={currentTheme}
          />
        )}

        <PhotoSubmissionModal 
          key={`photo-modal-${showPhotoModal}`}
          isOpen={showPhotoModal}
          onClose={() => setShowPhotoModal(false)}
        />

        {showCreditsModal && (
          <CreditsModal 
            isOpen={showCreditsModal}
            onClose={() => setShowCreditsModal(false)}
          />
        )}

        {showLeavePage && (
          <LeavePageModal 
            onLeave={handleLeavePage}
            onStay={handleStayOnPage}
          />
        )}

        {showPostScore && (
          <PostScoreModal 
            currentUser={currentUser}
            timeCompleted={finalScore}
            theme={currentTheme}
            gameId={gameId}
            placement={userPlacement}
          />
        )}


        {showMessage && (
          <div className={`animated-message ${messageType}`}>
            {message}
          </div>
        )}

        <div className="game-wrapper">
          <div className="instructions">
            <div className="current-theme">
              {currentTheme === 'theme1' ? 'Theme 1 - NicoleCodeGirl' : 'Theme 2 - Community'}
            </div>
          </div>

          <div className="cards-grid">
            {cards.map(card => (
              <Card
                key={card.id}
                id={card.id}
                photo={card.photo}
                isFlipped={flippedCards.includes(card.id)}
                isMatched={matchedCards.includes(card.id)}
                onClick={() => handleCardClick(card.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryGame;